import React, { useState, useEffect, createContext } from "react";
import {
  Box,
  Icon,
  SimpleGrid,
  Flex,
  Button,
  useColorModeValue,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Card,
  Grid,
  GridItem,
  Divider,
  Accordion,
  Badge,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
// Custom components
import {
  MdLocalDining,
  MdArrowDropDown,
  MdAttachMoney,
  MdOutlineTableBar,
  MdRefresh,
  MdShoppingCart,
  MdInsights,
  MdRestaurantMenu,
  MdPeople,
  MdInventory,
  MdCampaign,
  MdFeedback,
  MdTask,
  MdTableBar,
  MdTrendingUp,
  MdStar,
  MdLoop,
  MdSentimentSatisfied,
  MdAccessTime,
  MdQrCodeScanner,
} from "react-icons/md";
import Usa from "../../../assets/img/dashboards/usa.png";
import TrFlag from "../../../assets/img/dashboards/tr.jpeg";
import Eur from "../../../assets/img/dashboards/eur.png";

// Kullanılan bileşenler
import CampaignEffectiveness from "./components/CampaignEffectiveness";
import CampaignComparison from "./components/CampaignComparison";
import CampaignVisualizations from "./components/CampaignVisualizations";
import CampaignRecommendations from "./components/CampaignRecommendations";
import StatisticCard from "./components/StatisticCard";
import OrderAnalytics from "./components/OrderAnalytics";
import MenuPerformance from "./components/MenuPerformance";
import SimpleFeedback from "./components/SimpleFeedback";
import CustomerAnalytics from "./components/CustomerAnalytics";
import StockStatus from "./components/StockStatus";
import EmployeeAndTaskAnalysis from "./components/EmployeeAndTaskAnalysis";
import RevenueAnalysis from "./components/RevenueAnalysis";
import PriceAnalysis from "./components/PriceAnalysis";
import QRScanAnalytics from "./components/TableUsageAnalysis";
import PremiumUpgradeModal from "../../../components/modals/PremiumUpgradeModal";

// Servisler
import { orderService } from "../../../services/orderService";
import { tableService } from "../../../services/tableService";
import { pricingService } from "../../../services/pricingService";
import { authService } from "../../../services/authService";
import { useLocation } from "react-router-dom";

// Para birimi context'i
export const CurrencyContext = createContext();

export default function Dashboard() {
  // State tanımlamaları
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState("currentMonth");
  const [analyticsData, setAnalyticsData] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [orderSizes, setOrderSizes] = useState(null);
  const [maxOrderAmount, setMaxOrderAmount] = useState(0);
  const [totalTables, setTotalTables] = useState(0);
  const [occupiedTables, setOccupiedTables] = useState(0);
  const [currencySymbol, setCurrencySymbol] = useState("₺");
  const [userPlan, setUserPlan] = useState('STANDARD');
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [accordionIndex, setAccordionIndex] = useState(0);
  const currentUser = authService.getCurrentUser();
  const isStaff = currentUser?.role === 'staff' && currentUser?.userType === 'employee';

  // Renk değişkenleri
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const brandColor = useColorModeValue("brand.500", "brand.400");
  const accordionBg = useColorModeValue("white", "navy.700");
  const accordionHoverBg = useColorModeValue("gray.50", "navy.800");

  const location = useLocation();

  // Veri getirme fonksiyonu
  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Sipariş istatistiklerini getir
      const orderStats = await orderService.getOrderStats(selectedTimeRange);
      setOrderData(orderStats);
      setOrderSizes(orderStats.orderSizes);
      setMaxOrderAmount(orderStats.maxOrderAmount);
      
      // RevenueAnalysis'e veri gönder
      if (orderStats.monthlyRevenues) {
        setAnalyticsData(prev => ({
          ...prev,
          monthlyRevenues: orderStats.monthlyRevenues,
          yearlyRevenue: orderStats.yearlyRevenue
        }));
      }

      // Masa verilerini getir (sadece görev ve masa analizi için)
      try {
        const tables = await tableService.getTables();
        const totalTables = tables.length || 0;
        const occupiedTables = tables.filter(t => t.status === 'occupied').length || 0;
        
        setTotalTables(totalTables);
        setOccupiedTables(occupiedTables);

        setAnalyticsData(prev => ({
          ...prev,
          tables: {
            turnoverRate: 0, // Bu veri backend'den gelmeli
            occupancyByHour: [], // Bu veri backend'den gelmeli
            averageSeatingTime: 0 // Bu veri backend'den gelmeli
          }
        }));
      } catch (error) {
        console.error("Masa verileri getirilirken hata:", error);
        setTotalTables(0);
        setOccupiedTables(0);
      }


      setIsLoading(false);
    } catch (error) {
      console.error("Veri getirme hatası:", error);
      setIsLoading(false);
    }
  };

  // Zaman aralığı değiştiğinde veriyi yeniden getir
  useEffect(() => {
    fetchData();
  }, [selectedTimeRange]);

  // Plan bilgisini çek
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const pricingInfo = await pricingService.getPricingInfo();
        if (pricingInfo?.success && pricingInfo?.data?.business?.plan) {
          setUserPlan(pricingInfo.data.business.plan);
        }
      } catch (error) {
        console.error('Plan bilgisi alınamadı:', error);
        const business = JSON.parse(localStorage.getItem('business') || '{}');
        if (business.plan) {
          setUserPlan(business.plan);
        }
      }
    };
    
    fetchPlan();
  }, []);

  // URL'deki ?section parametresine göre hangi akordeonun açık olacağını belirle
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get("section");

    // Akordeon sırasına göre index haritası
    // 0: Gelir Analizi
    // 1: Sipariş Analizi
    // 2: Menü Performansı
    // 3: Masa Kullanım Analizi
    // 4: Stok Durumu
    // 5: Kampanya Analizi
    // 6: Müşteri Analizi
    // 7: Çalışan & Görev Analizi
    const sectionMap = {
      orders: 1,
      "menu-performance": 2,
      "table-usage": 3,
      stock: 4,
      campaigns: 5,
      customer: 6,
      "employee-tasks": 7,
    };

    if (section && sectionMap[section] !== undefined) {
      setAccordionIndex(sectionMap[section]);
    } else {
      setAccordionIndex(0);
    }
  }, [location.search]);

  // Para birimi değiştirme fonksiyonu
  const changeCurrency = (symbol) => {
    setCurrencySymbol(symbol);
  };

  return (
    <CurrencyContext.Provider value={currencySymbol}>
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Box mb="20px">
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "start", md: "center" }}
            minHeight="60px"
            w="100%"
          >
            <Flex
              align="center"
              mb={{ base: "10px", md: "0px" }}
            >
              <Icon as={MdInsights} color={brandColor} w="32px" h="32px" me="10px" />
              <Text
                color={textColor}
                fontSize="22px"
                fontWeight="700"
                lineHeight="100%"
              >
                İşletme Analizi
              </Text>
            </Flex>

            <Flex align="center">
              <Button
                leftIcon={<MdRefresh />}
                colorScheme="brand"
                variant="solid"
                size="sm"
                onClick={fetchData}
                isLoading={isLoading}
              >
                Yenile
              </Button>
            </Flex>
          </Flex>
        </Box>

        <Box>
          {isLoading ? (
            <Flex justify="center" align="center" h="60vh">
              <Spinner size="xl" color="brand.500" />
            </Flex>
          ) : (
            <>
              <SimpleGrid columns={{ base: 1, md: 2, lg: isStaff ? 2 : 4 }} spacing="20px" mb="20px">
                <StatisticCard
                  title="Toplam Sipariş"
                  value={orderData?.totalOrders?.toLocaleString() || 0}
                  icon={<Icon as={MdShoppingCart} w="35px" h="35px" />}
                  iconBg="#e45858"
                />
                {!isStaff && (
                  <StatisticCard
                    title="Günlük Satış"
                    value={`${currencySymbol}${(orderData?.dailyEarnings || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}`}
                    icon={<Icon as={MdAttachMoney} w="35px" h="35px" />}
                    iconBg="#e45858"
                  />
                )}
                <StatisticCard
                  title="Satılan Ürün"
                  value={orderData?.dailySoldItems?.toLocaleString() || 0}
                  icon={<Icon as={MdLocalDining} w="35px" h="35px" />}
                  iconBg="#e45858"
                />
                {!isStaff && (
                  <StatisticCard
                    title="Aylık Ciro"
                    value={`${currencySymbol}${(orderData?.monthlyRevenue || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}`}
                    icon={<Icon as={MdTrendingUp} w="35px" h="35px" />}
                    iconBg="#364fab"
                  />
                )}
              </SimpleGrid>
              
              {/*<SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing="20px" mb="20px">
                <StatisticCard
                  title="Ortalama Sipariş"
                  value={`${currencySymbol}${(orderData?.averageAmount || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}`}
                  icon={<Icon as={MdTrendingUp} w="35px" h="35px" />}
                  iconBg="#e45858"
                />
                <StatisticCard
                  title="En Çok Satan"
                  value={orderData?.topSellingItem?.name || "Veri Yok"}
                  icon={<Icon as={MdStar} w="35px" h="35px" />}
                  iconBg="#e45858"
                />
                <StatisticCard
                  title="Yoğun Saatler"
                  value={orderData?.peakHours?.[0]?.time || "Veri Yok"}
                  icon={<Icon as={MdAccessTime} w="35px" h="35px" />}
                  iconBg="#e45858"
                />
                <StatisticCard
                  title="Müşteri Memnuniyeti"
                  value={`${orderData?.averageRating?.toFixed(1) || 0}/5`}
                  icon={<Icon as={MdSentimentSatisfied} w="35px" h="35px" />}
                  iconBg="#e45858"
                />
              </SimpleGrid>*/}

              <Accordion
                allowMultiple={false}
                index={accordionIndex}
                onChange={(index) => {
                  // Chakra tek bir index veya dizi gönderebiliyor; biz tekli kullanıyoruz
                  const nextIndex = Array.isArray(index) ? index[0] : index;
                  setAccordionIndex(nextIndex);
                }}
                mb="20px"
              >

                {/* Gelir Analizi - Staff için gizli */}
                {!isStaff && (
                  <AccordionItem border="none" mb={4}>
                    <h2>
                      <AccordionButton 
                        bg={accordionBg} 
                        borderRadius="md"
                        _hover={{ bg: accordionHoverBg }}
                      >
                        <Flex align="center" flex="1">
                          <Icon as={MdAttachMoney} mr={2} color={brandColor} />
                          <Text fontWeight="bold">Gelir Analizi</Text>
                        </Flex>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4} pt={4}>
                      <SimpleGrid columns={{ base: 1, md: 1 }} spacing="20px" mb="20px">
                        <GridItem>
                          <RevenueAnalysis 
                            monthlyRevenues={analyticsData?.monthlyRevenues || orderData?.monthlyRevenues || []}
                            yearlyRevenue={analyticsData?.yearlyRevenue || orderData?.yearlyRevenue || 0}
                          />
                        </GridItem>
                      </SimpleGrid>
                    </AccordionPanel>
                  </AccordionItem>
                )}
                
                {/* Sipariş Analizi */}
                <AccordionItem border="none" mb={4}>
                  <h2>
                    <AccordionButton 
                      bg={accordionBg} 
                      borderRadius="md"
                      _hover={{ bg: accordionHoverBg }}
                    >
                      <Flex align="center" flex="1">
                        <Icon as={MdShoppingCart} mr={2} color={brandColor} />
                        <Text fontWeight="bold">Sipariş Analizi</Text>
                      </Flex>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} pt={4}>
                    <SimpleGrid columns={{ base: 1, md: 1 }} spacing="20px" mb="20px">
                      <GridItem>
                        <OrderAnalytics 
                          orderData={orderData} 
                          currency={currencySymbol} 
                          orderSizes={orderSizes}
                          maxOrderAmount={maxOrderAmount}
                          tipFeedback={orderData?.tipFeedback}
                          weeklyDayTraffic={orderData?.weeklyDayTraffic}
                          dayHourTraffic={orderData?.dayHourTraffic}
                          topDayHour={orderData?.topDayHour}
                        />
                      </GridItem>
                    </SimpleGrid>
                  </AccordionPanel>
                </AccordionItem>

                

                {/* Menü Performansı - Staff için gizli */}
                {!isStaff && (
                  <AccordionItem border="none" mb={4} opacity={userPlan !== 'PREMIUM' ? 0.6 : 1}>
                    <h2>
                      <AccordionButton 
                        bg={accordionBg} 
                        borderRadius="md"
                        _hover={{ bg: accordionHoverBg }}
                        isDisabled={userPlan !== 'PREMIUM'}
                      >
                        <Flex align="center" flex="1" as="div">
                          <Icon as={MdRestaurantMenu} mr={2} color={brandColor} />
                          <Text fontWeight="bold" as="span">Menü Performansı</Text>
                          {userPlan !== 'PREMIUM' && (
                            <Badge ml={2} colorScheme="yellow" fontSize="xs">
                              PREMIUM
                            </Badge>
                          )}
                        </Flex>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4} pt={4}>
                      {userPlan === 'PREMIUM' ? (
                        <SimpleGrid columns={{ base: 1, md: 1 }} spacing="20px" mb="20px">
                          <GridItem>
                            <MenuPerformance />
                          </GridItem>
                        </SimpleGrid>
                      ) : (
                        <Card p={6}>
                          <Flex direction='column' height="100%" justify="center" align="center" textAlign="center">
                            <Icon as={MdRestaurantMenu} w='48px' h='48px' color={brandColor} mb='16px' />
                            <Text color={textColor} fontSize='lg' fontWeight='700' mb='8px'>
                              Menü Performansı
                            </Text>
                            <Text color="gray.500" fontSize='sm' mb='16px'>
                              Menü performans analizi özelliğine erişmek için PREMIUM plana yükseltin.
                            </Text>
                            <Button 
                              colorScheme="brand" 
                              size="sm"
                              onClick={() => setIsUpgradeModalOpen(true)}
                            >
                              PREMIUM'a Yükselt
                            </Button>
                          </Flex>
                        </Card>
                      )}
                    </AccordionPanel>
                  </AccordionItem>
                )}

                {/* Masa Kullanım Analizi */}
                <AccordionItem border="none" mb={4} opacity={userPlan !== 'PREMIUM' ? 0.6 : 1}>
                  <h2>
                    <AccordionButton 
                      bg={accordionBg} 
                      borderRadius="md"
                      _hover={{ bg: accordionHoverBg }}
                      isDisabled={userPlan !== 'PREMIUM'}
                    >
                      <Flex align="center" flex="1" as="div">
                        <Icon as={MdQrCodeScanner} mr={2} color={brandColor} />
                        <Text fontWeight="bold" as="span">Masa Kullanım Analizi</Text>
                        {userPlan !== 'PREMIUM' && (
                          <Badge ml={2} colorScheme="yellow" fontSize="xs">
                            PREMIUM
                          </Badge>
                        )}
                      </Flex>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} pt={4}>
                    {userPlan === 'PREMIUM' ? (
                      <SimpleGrid columns={{ base: 1, md: 1 }} spacing="20px" mb="20px">
                        <GridItem>
                          <QRScanAnalytics />
                        </GridItem>
                      </SimpleGrid>
                    ) : (
                      <Card p={6}>
                        <Flex direction='column' height="100%" justify="center" align="center" textAlign="center">
                          <Icon as={MdQrCodeScanner} w='48px' h='48px' color={brandColor} mb='16px' />
                          <Text color={textColor} fontSize='lg' fontWeight='700' mb='8px'>
                            Masa Kullanım Analizi
                          </Text>
                          <Text color="gray.500" fontSize='sm' mb='16px'>
                            Masa kullanım analizi özelliğine erişmek için PREMIUM plana yükseltin.
                          </Text>
                          <Button 
                            colorScheme="brand" 
                            size="sm"
                            onClick={() => setIsUpgradeModalOpen(true)}
                          >
                            PREMIUM'a Yükselt
                          </Button>
                        </Flex>
                      </Card>
                    )}
                  </AccordionPanel>
                </AccordionItem>

                {/* Stok Durumu */}
                <AccordionItem border="none" mb={4} opacity={userPlan !== 'PREMIUM' ? 0.6 : 1}>
                  <h2>
                    <AccordionButton 
                      bg={accordionBg} 
                      borderRadius="md"
                      _hover={{ bg: accordionHoverBg }}
                      isDisabled={userPlan !== 'PREMIUM'}
                    >
                      <Flex align="center" flex="1" as="div">
                        <Icon as={MdInventory} mr={2} color={brandColor} />
                        <Text fontWeight="bold" as="span">Stok Durumu</Text>
                        {userPlan !== 'PREMIUM' && (
                          <Badge ml={2} colorScheme="yellow" fontSize="xs">
                            PREMIUM
                          </Badge>
                        )}
                      </Flex>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} pt={4}>
                    {userPlan === 'PREMIUM' ? (
                      <GridItem colSpan={{ base: 1, md: 2, lg: 4 }}>
                        <StockStatus />
                      </GridItem>
                    ) : (
                      <Card p={6}>
                        <Flex direction='column' height="100%" justify="center" align="center" textAlign="center">
                          <Icon as={MdInventory} w='48px' h='48px' color={brandColor} mb='16px' />
                          <Text color={textColor} fontSize='lg' fontWeight='700' mb='8px'>
                            Stok Durumu
                          </Text>
                          <Text color="gray.500" fontSize='sm' mb='16px'>
                            Stok takibi özelliğine erişmek için PREMIUM plana yükseltin.
                          </Text>
                          <Button 
                            colorScheme="brand" 
                            size="sm"
                            onClick={() => setIsUpgradeModalOpen(true)}
                          >
                            PREMIUM'a Yükselt
                          </Button>
                        </Flex>
                      </Card>
                    )}
                  </AccordionPanel>
                </AccordionItem>

                {/* Kampanya Analizi */}
                <AccordionItem border="none" mb={4}>
                  <h2>
                    <AccordionButton 
                      bg={accordionBg} 
                      borderRadius="md"
                      _hover={{ bg: accordionHoverBg }}
                    >
                      <Flex align="center" flex="1">
                        <Icon as={MdCampaign} mr={2} color={brandColor} />
                        <Text fontWeight="bold">Kampanya Analizi</Text>
                      </Flex>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} pt={4}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px" mb="20px">
                      <GridItem>
                        <CampaignEffectiveness />
                      </GridItem>
                      <GridItem>
                        <CampaignComparison />
                      </GridItem>
                    </SimpleGrid>
                    
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                      <GridItem>
                        <CampaignVisualizations />
                      </GridItem>
                      <GridItem>  
                        <CampaignRecommendations /> 
                      </GridItem>
                    </SimpleGrid>
                  </AccordionPanel>
                </AccordionItem>

                {/* Müşteri Analizi */}
                <AccordionItem border="none" mb={4} opacity={userPlan !== 'PREMIUM' ? 0.6 : 1}>
                  <h2>
                    <AccordionButton 
                      bg={accordionBg} 
                      borderRadius="md"
                      _hover={{ bg: accordionHoverBg }}
                      isDisabled={userPlan !== 'PREMIUM'}
                    >
                      <Flex align="center" flex="1" as="div">
                        <Icon as={MdSentimentSatisfied} mr={2} color={brandColor} />
                        <Text fontWeight="bold" as="span">Müşteri Analizi</Text>
                        {userPlan !== 'PREMIUM' && (
                          <Badge ml={2} colorScheme="yellow" fontSize="xs">
                            PREMIUM
                          </Badge>
                        )}
                      </Flex>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} pt={4}>
                    {userPlan === 'PREMIUM' ? (
                      <>
                        <Box mb={6}>
                          <CustomerAnalytics />
                        </Box>
                        <Box>
                          <SimpleFeedback />
                        </Box>
                      </>
                    ) : (
                      <Card p={6}>
                        <Flex direction='column' height="100%" justify="center" align="center" textAlign="center">
                          <Icon as={MdSentimentSatisfied} w='48px' h='48px' color={brandColor} mb='16px' />
                          <Text color={textColor} fontSize='lg' fontWeight='700' mb='8px'>
                            Müşteri Analizi
                          </Text>
                          <Text color="gray.500" fontSize='sm' mb='16px'>
                            Müşteri analizi özelliklerine erişmek için PREMIUM plana yükseltin.
                          </Text>
                          <Button 
                            colorScheme="brand" 
                            size="sm"
                            onClick={() => setIsUpgradeModalOpen(true)}
                          >
                            PREMIUM'a Yükselt
                          </Button>
                        </Flex>
                      </Card>
                    )}
                  </AccordionPanel>
                </AccordionItem>

                {/* Çalışan & Görev Analizi - Staff için gizli */}
                {!isStaff && (
                  <AccordionItem border="none" mb={4} opacity={userPlan !== 'PREMIUM' ? 0.6 : 1}>
                    <h2>
                      <AccordionButton 
                        bg={accordionBg} 
                        borderRadius="md"
                        _hover={{ bg: accordionHoverBg }}
                        isDisabled={userPlan !== 'PREMIUM'}
                      >
                        <Flex align="center" flex="1" as="div">
                          <Icon as={MdTask} mr={2} color={brandColor} />
                          <Text fontWeight="bold" as="span">Çalışan & Görev Analizi</Text>
                          {userPlan !== 'PREMIUM' && (
                            <Badge ml={2} colorScheme="yellow" fontSize="xs">
                              PREMIUM
                            </Badge>
                          )}
                        </Flex>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4} pt={4}>
                      {userPlan === 'PREMIUM' ? (
                        <EmployeeAndTaskAnalysis />
                      ) : (
                        <Card p={6}>
                          <Flex direction='column' height="100%" justify="center" align="center" textAlign="center">
                            <Icon as={MdTask} w='48px' h='48px' color={brandColor} mb='16px' />
                            <Text color={textColor} fontSize='lg' fontWeight='700' mb='8px'>
                              Çalışan & Görev Analizi
                            </Text>
                            <Text color="gray.500" fontSize='sm' mb='16px'>
                              Çalışan & görev analizi özelliklerine erişmek için PREMIUM plana yükseltin.
                            </Text>
                            <Button 
                              colorScheme="brand" 
                              size="sm"
                              onClick={() => setIsUpgradeModalOpen(true)}
                            >
                              PREMIUM'a Yükselt
                            </Button>
                          </Flex>
                        </Card>
                      )}
                    </AccordionPanel>
                  </AccordionItem>
                )}

                {/* Masa Analizi - Şimdilik yorum satırında */}
                {/* <AccordionItem border="none" mb={4} opacity={userPlan !== 'PREMIUM' ? 0.6 : 1}>
                  <h2>
                    <AccordionButton 
                      bg={accordionBg} 
                      borderRadius="md"
                      _hover={{ bg: accordionHoverBg }}
                      isDisabled={userPlan !== 'PREMIUM'}
                    >
                      <Flex align="center" flex="1">
                        <Icon as={MdTableBar} mr={2} color={brandColor} />
                        <Text fontWeight="bold">Masa Analizi</Text>
                        {userPlan !== 'PREMIUM' && (
                          <Badge ml={2} colorScheme="yellow" fontSize="xs">
                            PREMIUM
                          </Badge>
                        )}
                      </Flex>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} pt={4}>
                    {userPlan === 'PREMIUM' ? (
                      <TableOccupancy 
                        tableData={{
                          total: totalTables || 0,
                          occupied: occupiedTables || 0,
                          available: (totalTables - occupiedTables) || 0,
                          occupancyRate: totalTables > 0 ? ((occupiedTables / totalTables) * 100) : 0,
                          turnoverRate: analyticsData?.tables?.turnoverRate || 0,
                          occupancyByHour: analyticsData?.tables?.occupancyByHour || [],
                          averageSeatingTime: analyticsData?.tables?.averageSeatingTime || 0
                        }}
                      />
                    ) : (
                      <Card p={6}>
                        <Flex direction='column' height="100%" justify="center" align="center" textAlign="center">
                          <Icon as={MdTableBar} w='48px' h='48px' color={brandColor} mb='16px' />
                          <Text color={textColor} fontSize='lg' fontWeight='700' mb='8px'>
                            Masa Analizi
                          </Text>
                          <Text color="gray.500" fontSize='sm' mb='16px'>
                            Masa analizi özelliklerine erişmek için PREMIUM plana yükseltin.
                          </Text>
                          <Button 
                            colorScheme="brand" 
                            size="sm"
                            onClick={() => setIsUpgradeModalOpen(true)}
                          >
                            PREMIUM'a Yükselt
                          </Button>
                        </Flex>
                      </Card>
                    )}
                  </AccordionPanel>
                </AccordionItem> */}

                {/* Fiyatlandırma Bilgileri - Staff için gizli */}
                {!isStaff && (
                  <AccordionItem border="none" mb={4}>
                    <h2>
                      <AccordionButton 
                        bg={accordionBg} 
                        borderRadius="md"
                        _hover={{ bg: accordionHoverBg }}
                      >
                        <Flex align="center" flex="1">
                          <Icon as={MdAttachMoney} mr={2} color={brandColor} />
                          <Text fontWeight="bold">Fiyatlandırma Bilgileri</Text>
                        </Flex>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4} pt={4}>
                      <PriceAnalysis />
                    </AccordionPanel>
                  </AccordionItem>
                )}
              </Accordion>
            </>
          )}
        </Box>
        
        <PremiumUpgradeModal
          isOpen={isUpgradeModalOpen}
          onClose={() => setIsUpgradeModalOpen(false)}
          onConfirm={async () => {
            // Plan bilgisini güncelle
            const pricingInfo = await pricingService.getPricingInfo();
            if (pricingInfo?.success && pricingInfo?.data?.business?.plan) {
              setUserPlan(pricingInfo.data.business.plan);
            }
          }}
        />
      </Box>
    </CurrencyContext.Provider>
  );
}
